import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DialogService as PrimeNgDialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { debounceTime, map, Observable, of, Subject, Subscription, switchMap } from 'rxjs';
import { FASDynamicTableColumnConfig } from 'fas-services';
import { BookAccessGeneratorService } from '../../../api/api-services/book-access-generator.service';
import { BookAccessHistoryService } from '../../../api/api-services/book-access-history.service';
import { BookAccessDetailsService } from '../../../api/api-services/book-access-details.service';
import { OsksService } from '../../../api/api-services/osks.service';
import { IBookAccessHistoryMetaVm } from '../../../api/book-access-history-meta-vm.model';
import { IPaginatedResultBookAccessHistoryMetaVm } from '../../../api/paginated-result-book-access-history-meta-vm.model';
import { IOskVm } from '../../../api/osk-vm.model';
import { SortOrder } from '../../../api/sort-order.model';
import { DialogService } from '../../shared/services/dialog.service';
import { centerDynamicModalConfig } from '../../shared/helpers/modal.config';
import { eModalRoles } from '../../shared/models/modals';
import { BookAccessPackageEditComponent } from './book-access-package-edit/book-access-package-edit.component';

@Component({
  selector: 'app-generate-users',
  templateUrl: './generate-users.component.html',
  styleUrl: './generate-users.component.scss',
})
export class GenerateUsersComponent implements OnInit, OnDestroy {
  private _bookAccessGeneratorService = inject(BookAccessGeneratorService);
  private _bookAccessHistoryService = inject(BookAccessHistoryService);
  private _bookAccessDetailsService = inject(BookAccessDetailsService);
  private _osksService = inject(OsksService);
  private _messageService = inject(MessageService);
  private _primengDialogService = inject(PrimeNgDialogService);
  private _customDialogService = inject(DialogService);
  private _fb = inject(FormBuilder);

  public ID_PACKAGE_DETAILS = 'ID_BOOK_ACCESS_PACKAGE_DETAILS';
  public readonly DEFAULT_VALID_DAYS = 240;
  public minValidUntil = this.addDays(this.today(), 1);

  public generatorForm!: FormGroup;
  public loading = false;
  public filteredOsks: IOskVm[] = [];
  public selectedOsk: IOskVm | null = null;

  public historyList!: IBookAccessHistoryMetaVm[];
  public historyIndex!: number;
  public historyItem!: IBookAccessHistoryMetaVm;
  public resetTrigger = new Subject<void>();

  private ref: DynamicDialogRef | undefined;
  private subscriptions: Subscription[] = [];

  ngOnInit(): void {
    this.generatorForm = this._fb.group({
      requestedUsersCount: [20],
      noTimeLimit: [false],
      validDays: [this.DEFAULT_VALID_DAYS],
      validUntil: [this.addDays(this.today(), this.DEFAULT_VALID_DAYS)],
      oskSearch: [''],
    });

    const validDaysSubscription = this.generatorForm
      .get('validDays')!
      .valueChanges.subscribe((days: number | null) => this.syncValidUntilFromDays(days));
    this.subscriptions.push(validDaysSubscription);

    const validUntilSubscription = this.generatorForm
      .get('validUntil')!
      .valueChanges.subscribe((date: Date | null) => this.syncDaysFromValidUntil(date));
    this.subscriptions.push(validUntilSubscription);

    const oskSearchSubscription = this.generatorForm
      .get('oskSearch')!
      .valueChanges.pipe(
        debounceTime(300),
        switchMap((query) => {
          if (!query) {
            return of([]);
          }
          return this._osksService.get(query, '').pipe(map((res) => res.data ?? []));
        })
      )
      .subscribe((osks) => (this.filteredOsks = osks));
    this.subscriptions.push(oskSearchSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    if (this.ref) {
      this.ref.close();
      this.ref = undefined;
    }
  }

  private today(): Date {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  private startOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
  }

  /** Liczba dni ważności -> data ważności (dzisiaj + dni). Nie emituje zdarzenia, żeby nie zapętlić synchronizacji. */
  private syncValidUntilFromDays(days: number | null): void {
    const validUntil = days && days > 0 ? this.addDays(this.today(), days) : null;
    this.generatorForm.get('validUntil')!.setValue(validUntil, { emitEvent: false });
  }

  /** Data ważności -> liczba dni ważności (data - dzisiaj). Nie emituje zdarzenia, żeby nie zapętlić synchronizacji. */
  private syncDaysFromValidUntil(date: Date | null): void {
    if (!date) {
      this.generatorForm.get('validDays')!.setValue(null, { emitEvent: false });
      return;
    }
    const msInDay = 24 * 60 * 60 * 1000;
    const days = Math.round((this.startOfDay(date).getTime() - this.today().getTime()) / msInDay);
    this.generatorForm.get('validDays')!.setValue(days > 0 ? days : 1, { emitEvent: false });
    if (days <= 0) {
      this.generatorForm.get('validUntil')!.setValue(this.addDays(this.today(), 1), { emitEvent: false });
    }
  }

  onOskSelect(event: AutoCompleteSelectEvent): void {
    this.selectedOsk = event.value;
  }

  clearOsk(): void {
    this.selectedOsk = null;
    this.generatorForm.patchValue({ oskSearch: '' });
  }

  fetchHistory = (params: {
    page: number;
    pageSize: number;
    sortField?: keyof IBookAccessHistoryMetaVm;
  }): Observable<{ data: IBookAccessHistoryMetaVm[]; total: number }> => {
    const { page, pageSize, sortField = ''} = params;
    return this._bookAccessHistoryService.get(sortField, page, pageSize, SortOrder.Descending).pipe(
      map((result: IPaginatedResultBookAccessHistoryMetaVm) => ({
        data: result.data ?? [],
        total: result.allElementsCount,
      }))
    );
  };

  handleHistoryLoaded(data: IBookAccessHistoryMetaVm[]): void {
    this.historyList = data;
  }

  public historyColumns: FASDynamicTableColumnConfig<IBookAccessHistoryMetaVm>[] = [
    { field: 'created', columnHeader: 'Data', columnTemplateKey: 'dateTemplate' },
    {
      field: 'accessGeneratedCount',
      columnHeader: 'Ilość kont',
      columnClass: 'flex align-items-center gap-2',
      actions: [
        {
          label: 'Pokaż listę',
          buttonClass: 'white-button appear',
          icoSuffix: 'pi pi-arrow-right',
          callback: (data: IBookAccessHistoryMetaVm, rowIndex: number) => this.showDetails(data, rowIndex),
        },
      ],
    },
    { field: 'validDays', columnHeader: 'Ważność konta (dni)', columnTemplateKey: 'validDaysTemplate' },
    { field: 'createdBy', columnHeader: 'Utworzył' },
    { field: 'oskName', columnHeader: 'Przypisany OSK', columnTemplateKey: 'oskNameTemplate' },
    { field: 'printed', columnHeader: 'Potwierdzenie wydruku', columnTemplateKey: 'printedTemplate' },
    { field: 'printedDate', columnHeader: 'Data wydruku', columnTemplateKey: 'printedDateTemplate' },
    {
      columnHeader: 'Akcje',
      columnClass: '',
      actions: [
        {
          label: '',
          tooltip: 'Edytuj',
          buttonClass: 'pi pi-pencil cursor-pointer text-center appear transparent-button',
          callback: (data: IBookAccessHistoryMetaVm) => this.openEdit(data),
        },
        {
          label: '',
          tooltip: 'Usuń',
          buttonClass: 'pi pi-trash cursor-pointer text-center appear transparent-button',
          callback: (data: IBookAccessHistoryMetaVm) => this.deletePackage(data),
        },
      ],
    },
  ];

  generate(): void {
    if (!this.generatorForm.value.requestedUsersCount) {
      return;
    }

    this.loading = true;
    const noTimeLimit = this.generatorForm.value.noTimeLimit;
    this._bookAccessGeneratorService
      .post({
        requestedUsersCount: this.generatorForm.value.requestedUsersCount,
        timeLimit: !noTimeLimit,
        validDays: noTimeLimit ? undefined : this.generatorForm.value.validDays,
        oskCompanyGuid: this.selectedOsk?.guid,
      })
      .subscribe({
        next: (result) => {
          this.loading = false;
          this._messageService.add({
            severity: 'success',
            summary: 'Sukces',
            detail: `Wygenerowano ${result.accessTickets?.length ?? 0} kont dostępu.`,
          });
          this.resetTrigger.next();
          this.showDetailsByGuid(result.guid);
        },
        error: (err) => {
          this.loading = false;
          console.error('Book access generation error:', err);
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Wystąpił błąd. Spróbuj ponownie później.',
          });
        },
      });
  }

  showDetails(data: IBookAccessHistoryMetaVm, rowIndex: number): void {
    this.historyItem = { ...data };
    this.historyIndex = rowIndex;
    this._customDialogService.openDialog(this.ID_PACKAGE_DETAILS);
  }

  private showDetailsByGuid(packageGuid: string): void {
    const found = (this.historyList ?? []).findIndex((x) => x.guid === packageGuid);
    if (found >= 0) {
      this.showDetails(this.historyList[found], found);
    }
  }

  onPackageDetailsChanged(): void {
    this.resetTrigger.next();
  }

  openEdit(data: IBookAccessHistoryMetaVm): void {
    this.ref = this._primengDialogService.open(BookAccessPackageEditComponent, {
      ...centerDynamicModalConfig,
      data,
    });

    this.ref.onClose.subscribe((res: eModalRoles | undefined) => {
      if (res === eModalRoles.SUCCESS) {
        this.resetTrigger.next();
      }
    });
  }

  deletePackage(data: IBookAccessHistoryMetaVm): void {
    if (data.printed || (data.usedCount ?? 0) > 0) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Nie można usunąć',
        detail: data.printed
          ? 'Lista została oznaczona jako wydrukowana i nie może zostać usunięta.'
          : 'Co najmniej jeden kod z listy został wykorzystany — lista nie może zostać usunięta.',
      });
      return;
    }
    this._bookAccessDetailsService.delete(data.guid).subscribe({
      next: () => {
        this.resetTrigger.next();
      },
      error: (err) => {
        console.error('Book access package delete error:', err);
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Wystąpił błąd. Spróbuj ponownie później.',
        });
      },
    });
  }
}
